/**
 * Vesper For You Service (Shared)
 *
 * Discovery picks for the Discover screen — surfaces NEW people the user
 * hasn't connected with yet, ranked by the matching algorithm.
 */

import apiClient from './api.client';
import { API } from '../constants/api';
import { deduped } from './dedupedFetch';

export type ForYouSignal =
  | 'high_similarity'
  | 'shared_interests'
  | 'high_trust'
  | 'recently_active';

export interface ForYouPick {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  headline: string;
  city: string | null;
  trustScore: number;
  why: string;
  signal: ForYouSignal;
}

export interface ForYouResponse {
  weekLabel: string;
  featured: ForYouPick | null;
  featuredAngle: string | null;
  supporting: ForYouPick[];
}

export async function getWeekly(): Promise<ForYouResponse> {
  // 60s TTL — picks are time-bounded weekly anyway, and the AI cost on
  // recompute is non-trivial. No staleness concern at this cadence.
  return deduped('forYou:weekly', async () => {
    const res = await apiClient.get<{ data: ForYouResponse }>(API.FOR_YOU_WEEKLY);
    return res.data.data;
  }, 60_000);
}

// ── Event picks ──────────────────────────────────────────────────────────────

export interface ForYouEventPick {
  eventId: string;
  title: string;
  description: string | null;
  category: string | null;
  coverImageUrl: string | null;
  venueName: string | null;
  locationName: string | null;
  startTime: string;
  endTime: string | null;
  lat: number;
  lng: number;
  isFree: boolean;
  matchScore: number;
}

export interface ForYouEventResponse {
  weekLabel: string;
  featured: ForYouEventPick | null;
  supporting: ForYouEventPick[];
}

export async function getEventPicks(
  lat: number,
  lng: number,
  radiusMiles?: number,
): Promise<ForYouEventResponse> {
  // Quantize lat/lng to 3 decimals (~110m) so micro-moves don't bust the
  // cache. Same effective radius for ranking purposes.
  const qLat = lat.toFixed(3);
  const qLng = lng.toFixed(3);
  const qR = radiusMiles ?? 'def';
  return deduped(`forYou:events:${qLat}:${qLng}:${qR}`, async () => {
    const params = new URLSearchParams({ lat: String(lat), lng: String(lng) });
    if (radiusMiles != null) params.set('radiusMiles', String(radiusMiles));
    const res = await apiClient.get<{ data: ForYouEventResponse }>(
      `${API.FOR_YOU_EVENTS}?${params}`,
    );
    return res.data.data;
  }, 30_000);
}
